import React from 'react';
import { Row } from 'antd';
import evaluate from 'evaluator.js';
import { getIdMap } from 'ontraccr-common/lib/Common';
import { DateTime } from 'luxon';
import { Buckets } from 'ontraccr-common';
import { formatCardTitle } from '../../../boards/boardHelpers';
import { formatFormDropdownList, getLinkId, getPhaseCostcodeTreeData } from '../../formHelpers';
import { isObjectEmpty, sortByObjProperty } from '../../../common/helpers';
import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import { formatEquipmentLabelFromCompanySettings } from '../../../equipment/equipment.helpers';
import { currencyFormatter, currencyParser } from '../../../helpers/inputParsers';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import { getColumnTitle } from './FormFieldColumns';
import { isCustomDateColumn } from './tableColumnHelpers';
import CustomDateColumn from './CustomDateColumn';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';
import { getFormTableCostcodeUpdateFields } from '../../../helpers/costcodeHelpers';

// Check if there is a loop in calculations (e.g A -> B -> C -> A);
// If there is a loop we won't be able to do the calculations properly

// If we revist the current node while visiting it's parents, there must be a loop
const getLoopId = (sectionId, fieldId) => `${sectionId}.${fieldId}`;
const calculationFieldsHaveLoop = ({
  configProps = {},
  sections = [],
  field = {},
  sectionId,
  calculationColumn,
}) => {
  const { id: fieldId } = field;
  if (!fieldId) return false;
  const originalId = getLoopId(sectionId, fieldId);
  const sectionMap = {};
  sections.forEach((section) => {
    if (!(section.id in sectionMap)) sectionMap[section.id] = {};
    const { fields = [] } = section;
    fields.forEach((sectionField) => {
      sectionMap[section.id][sectionField.id] = sectionField;
    });
  });
  const { tags = [] } = configProps;
  let nodes = [...tags];
  // Visit all parent nodes to see if we repeat the original node
  while (nodes.length) {
    const node = nodes.pop();
    const {
      colKey: tagColKey,
      fieldId: tagFieldId,
      sectionId: tagSectionId,
    } = node;
    const tagLoopId = getLoopId(tagSectionId, tagFieldId);
    const isCalculationTable = calculationColumn && tagLoopId === originalId;
    if (
      (!isCalculationTable && tagLoopId === originalId)
      || (isCalculationTable && tagColKey === calculationColumn)
    ) return true;
    const {
      [tagSectionId]: {
        [tagFieldId]: {
          configProps: {
            tags: nextTags = [],
            tagsMap: nextTagsMap = {},
          } = {},
        } = {},
      } = {},
    } = sectionMap;
    if (tagColKey) {
      const colNextTags = nextTagsMap[tagColKey] || [];
      nodes = nodes.concat(colNextTags);
    } else {
      nodes = nodes.concat(nextTags);
    }
  }
  return false;
};

export const getFormulaError = ({
  configProps = {},
  sections = [],
  field = {},
  sectionId,
  calculationColumn,
}) => {
  if (calculationFieldsHaveLoop({
    configProps,
    sections,
    field,
    sectionId,
    calculationColumn,
  })) {
    return 'Calculation fields have a reference loop.';
  }
  const { formula = '', tags = [] } = configProps;
  let replacedFormula = formula;
  let offset = 0;
  tags.forEach((tag) => {
    const { startIndex, endIndex } = tag;
    replacedFormula = `${replacedFormula.substring(0, startIndex - offset)}1${replacedFormula.substring(endIndex - offset + 1)}`;
    offset += endIndex - startIndex;
  });
  try {
    evaluate(replacedFormula);
    return null;
  } catch (err) {
    return err.message;
  }
};

export const getTableFormulaError = ({
  configProps = {},
  sections = [],
  field = {},
  sectionId,
}) => {
  const { columns = [], formulaMap = {}, tagsMap = {} } = configProps;
  const isValid = columns.every((col = {}) => {
    const { key, isCalculation } = col;
    if (!isCalculation) return true;
    const formula = formulaMap[key];
    const tags = tagsMap[key] || [];
    const err = getFormulaError({
      configProps: { formula, tags },
      sections,
      field,
      sectionId,
      calculationColumn: key,
    });
    if (!formula?.length || err) return false;
    return true;
  });
  return !isValid;
};

const dataTypesThatRequireSubDataType = new Set(['CompletedForms', 'Cards', 'Buckets', 'PayableSubContracts']);

const isValidLink = (url = '') => url.match(/^(?:.{0,5}:)?\/\//);

export const fieldTypeCanSubmit = {
  'yes-no': ({ configProps }) => configProps.title,
  dropdown: ({ configProps }) => {
    const {
      numAnswers = 1,
    } = configProps;
    return (
      configProps.title
      && configProps.dataType
      && numAnswers
      && (
        !dataTypesThatRequireSubDataType.has(configProps.dataType)
        || !!configProps.subDataType
      )
      && (
        configProps.dataType !== 'Custom'
        || (
          configProps.customOptions
          && configProps.customOptions.length > 0
        )
      )
    );
  },
  text: ({ configProps }) => configProps.title,
  attachment: ({ configProps }) => configProps.title,
  table: ({
    configProps, sections, field, sectionId,
  }) => {
    const {
      title,
      columns,
      preloadProject,
      preloadDropdown,
      preloadForm,
      preloadFormDropdown,
      preloadSubmissionDropdown,
      preloadEntryDateType,
      preloadEntryDateField,
      preloadExistingEntries,
      preloadSovTemplateId,
      preloadSovSectionId,
      timecardPreloadTables,
      dataType,
      subDataType,
    } = configProps;
    const hasProject = preloadProject || preloadDropdown;
    return (
      title
      && columns
      && columns.length > 0
      && !getTableFormulaError({
        configProps, sections, field, sectionId,
      })
      && (preloadEntryDateType !== 'dateTime' || !!preloadEntryDateField)
      && (
        !preloadExistingEntries
        || dataType === 'TimeEntry'
        || (
          dataType === 'ScheduleOfValues'
          && hasProject
          && preloadSovTemplateId !== undefined
          && preloadSovSectionId
        )
        || (timecardPreloadTables?.length && hasProject)
        || (hasProject && (preloadForm || preloadFormDropdown || preloadSubmissionDropdown))
      )
      && (
        dataType !== 'Buckets'
        || subDataType
      )
    );
  },
  calculation: ({
    configProps, sections, field, sectionId,
  }) => (
    configProps.title
    && configProps.formula
    && configProps.formula.length > 0
    && !getFormulaError({
      configProps, sections, field, sectionId,
    })
  ),
  dateRange: ({ configProps }) => configProps.title,
  dateTime: ({ configProps }) => configProps.title,
  attribute: ({ configProps }) => configProps.title,
  payment: ({ configProps = {} }) => configProps.title && configProps.linkedField,
  multiSig: ({ configProps = {} }) => configProps.title,
  staticText: ({ configProps }) => (configProps.isHyperlink
    ? isValidLink(configProps.title)
    : configProps.title),
  staticAttachments: ({ configProps }) => configProps.title,
  gpsLocation: ({ configProps }) => configProps.title,
  weather: ({ configProps }) => configProps.title,
  dynamicAttribute: ({ configProps }) => (
    configProps.title
    && configProps.dataType
    && configProps.fieldId
    && (configProps.dataType !== 'equipment' || configProps.subType !== undefined)
  ),
  timeTrackingGrid: ({ configProps }) => configProps.title,
};

const premiumText = 'Available with subscription upgrade, contact your Ontraccr account manager for more information';
export const fieldOption = (field, disabled) => (
  <Row style={{ width: 330 }}>
    <Row className="form-field-type-title">
      {field.title}
    </Row>
    {disabled && (
      <Row className="form-field-type-premium">
        {premiumText}
      </Row>
    )}
    <Row className="form-field-type-description">
      {field.description}
    </Row>
  </Row>
);

const filterByDivision = (arr, divisionSet) => (
  arr.filter((item) => divisionSet.has(item.divisionId))

);

const combineSharedTreeTitles = (treeOptions) => {
  const parsedTreeOptions = {};

  treeOptions.forEach((option) => {
    const { title, children } = option;
    if (!parsedTreeOptions[title]) {
      parsedTreeOptions[title] = option;
    } else {
      parsedTreeOptions[title].children.push(...children);
    }
  });

  return Object.values(parsedTreeOptions);
};

export const getCostcodeDropdownOptions = ({
  costcodes,
  divisionSet,
  projectId,
  projectIds = [],
  phases,
  costcodeIdMap,
  projectIdMap,
  openLimit,
  numAnswers = 1,
  actualSelectedSet = new Set(),
  hideGlobal = false,
  isClockIn = false,
}) => {
  const divisionCostcodes = filterByDivision(costcodes, divisionSet);
  const data = hideGlobal
    ? []
    : getPhaseCostcodeTreeData({
      projectId,
      phases,
      costcodes: divisionCostcodes,
      costcodeMap: costcodeIdMap,
      projectMap: projectIdMap,
      disableOptions: !openLimit && numAnswers > 1 && actualSelectedSet.length >= numAnswers,
      isClockIn,
    });
  if (projectIds.length) {
    projectIds.forEach((id) => {
      const projectData = getPhaseCostcodeTreeData({
        projectId: id,
        phases,
        costcodes: divisionCostcodes,
        costcodeMap: costcodeIdMap,
        projectMap: projectIdMap,
        disableOptions: !openLimit && numAnswers > 1 && actualSelectedSet.length >= numAnswers,
        isClockIn,
      });
      data.push(...projectData);
    });
  }
  return combineSharedTreeTitles(data);
};

export const getContactDropdownOptions = ({
  contactAddressBooks = {},
  globalAddressBooks = {},
  linkField,
  linkedCustomerId,
  linkedVendorId,
  responding,
  actualSelected,
}) => {
  const customerAddressBook = contactAddressBooks[linkedCustomerId] ?? [];
  const vendorAddressBook = contactAddressBooks[linkedVendorId] ?? [];
  if (!responding) {
    return actualSelected ?? [];
  } if (!linkField) {
    return Object.values(globalAddressBooks);
  } if (linkedCustomerId === linkedVendorId) { // the same if linked
    return customerAddressBook;
  }
  return customerAddressBook.concat(vendorAddressBook);
};

// Make sure to mirror changes in DropdownField.jsx
// TODO: Refactor to combine logic
export const getDropDownOptions = ({
  // Base Inputs
  t = (val) => val,
  configProps = {},
  actualSelectedSet = new Set(),
  divisionSet = new Set(),
  filterLabels = [],
  responding = false,
  responses = {},
  settings = {},

  // Data Lists
  customers = [],
  projects = [],
  users = [],
  equipment = [],
  vendors = [],
  labels = [],
  forms = [],
  subContractForms = [],
  costcodes = [],
  phases = [],
  formTemplates = [],
  scheduleCategories = [],

  // subtypes
  projectTypes = [],
  equipmentTypes = [],

  // Maps
  projectIdMap = {},
  bucketTemplateMap = {},
  boardCardsForDropdown = {},
  subContractMap = {},
  contactAddressBooks = {},
  globalAddressBooks = {},
  projectEquipment = {},
  costcodeIdMap = {},
  divisionMap = {},
  vendorIdMap = {},
  boardsForDropdown = [],

  // Ids
  customerId = null,
  projectId = null,
  templateId = null,
  vendorId = null,

  // x to label
  customerToLabel = {},
  userToLabel = {},
  vendorToLabel = {},

  selectedBucket = null,
  selectedBucketTypeToIdMap = {},
  selectedBucketTypes = [],
}) => {
  let data = [];
  let sortProperty = 'name';
  let allData = [];
  let placeholder = 'Select an option';

  const {
    dataType,
    subDataType,
    openLimit,
    numAnswers = 1,
    linkField,
    enableGlobalSelection,
    customOptions,
    positionIds,
    boardCardTypeFilter,
    lockedFilterLabels = [],
  } = configProps;

  const linkedCustomerId = getLinkId({ responses, defaultValue: customerId, linkField });
  const linkedVendorId = getLinkId({ responses, defaultValue: vendorId, linkField });

  switch (dataType) {
    case 'Customers': {
      const {
        [projectId]: { customerId: projectCustomerId } = {},
      } = projectIdMap;
      data = customers.filter((customer) => {
        const {
          [customer.id]: customerLabels = [],
        } = customerToLabel;
        const customerLabelSet = new Set(customerLabels);

        return (
          customer.active
          && (!projectId || customer.id === projectCustomerId)
          && filterLabels.every((label) => customerLabelSet.has(label))
          && (!selectedBucket || isObjectEmpty(selectedBucket) || Buckets.getLinkedBuckets([selectedBucket], [customer.id], ['customers']).allBuckets.length > 0)
        );
      });
      placeholder = `Select a ${t('Customer')}`;
      break;
    }
    case 'Projects': {
      const projectTypeSet = new Set(projectTypes.map((type) => type.id));
      const subDataTypeSet = new Set(
        subDataType?.filter?.((typeId) => projectTypeSet.has(typeId) || typeId === null) ?? [],
      );
      sortProperty = 'label';
      data = projects.filter((project) => project.active
        && (divisionSet.has(project.divisionId))
        && (!customerId || project.customerId === customerId)
        && (!subDataTypeSet?.size || subDataTypeSet.has(project.projectTypeId))
        && (!selectedBucket || isObjectEmpty(selectedBucket) || Buckets.getLinkedBuckets([selectedBucket], [project.id], ['projects']).allBuckets.length > 0))
        .map(({ name, number, ...rest }) => ({
          label: formatProjectLabelFromCompanySettings({ name, number, settings }),
          name,
          number,
          ...rest,
        }));
      placeholder = `Select a ${t('Project')}`;
      break;
    }
    case 'Users': {
      const allUsers = Array.from(divisionSet).map((divisionId) => {
        const {
          [divisionId]: {
            users: divisionUsers = new Set(),
          } = {},
        } = divisionMap;
        return Array.from(divisionUsers);
      });
      const userSet = new Set(allUsers.flat());
      const divUsers = users.filter((user) => user.active && userSet.has(user.id));

      const hasLockedFilterLabels = lockedFilterLabels.length > 0;
      const userFilterLabels = hasLockedFilterLabels ? lockedFilterLabels : filterLabels;
      data = divUsers.filter((user) => {
        const positionSet = new Set(positionIds ?? []);
        const {
          [user.id]: userLabels = [],
        } = userToLabel;
        const userLabelSet = new Set(userLabels);
        return (
          user.active
          && (
            actualSelectedSet.has(user.id)
            || (
              (
                userFilterLabels.length === 0
                || (
                  hasLockedFilterLabels
                    ? userFilterLabels.some((label) => userLabelSet.has(label))
                    : userFilterLabels.every((label) => userLabelSet.has(label))
                )
              )
              && (positionSet.size === 0 || positionSet.has(user.positionId))
            )
          )
        );
      });
      placeholder = 'Select a User';
      break;
    }
    case 'Costcodes': {
      sortProperty = 'title';
      data = getCostcodeDropdownOptions({
        costcodes,
        divisionSet,
        projectId,
        phases,
        costcodeIdMap,
        projectIdMap,
        openLimit,
        numAnswers,
        actualSelectedSet,
        isClockIn: false,
      });
      placeholder = 'Select a Costcode';
      break;
    }
    case 'Equipment': {
      const equipmentTypeSet = new Set(equipmentTypes.map((type) => type.id));
      const subDataTypeSet = new Set(
        subDataType?.filter?.((typeId) => equipmentTypeSet.has(typeId) || typeId === null) ?? [],
      );
      data = equipment.filter((eq) => (
        eq.active
          && eq.divisionIds.some((divisionId) => divisionSet.has(divisionId))
          && (enableGlobalSelection || !projectId || !!projectEquipment[projectId]?.[eq.id])
          && (!subDataTypeSet?.size || subDataTypeSet.has(eq.equipmentTypeId))
      )).map((eq) => ({
        ...eq,
        label: formatEquipmentLabelFromCompanySettings({
          name: eq.name,
          code: eq.code,
          settings,
        }),
      }));
      placeholder = 'Select Equipment';
      break;
    }
    case 'Vendors': {
      data = vendors.filter((vendor) => {
        const {
          [vendor.id]: vendorLabels = [],
        } = vendorToLabel;
        const vendorLabelSet = new Set(vendorLabels);

        return (
          vendor.active
          && filterLabels.every((label) => vendorLabelSet.has(label))
        );
      });
      placeholder = 'Select Vendor';
      break;
    }
    case 'Sub-Contracts': {
      const {
        [projectId]: subcontracts = [],
      } = subContractMap;
      data = subcontracts
        .map((sub) => ({ id: sub.rowId, name: sub.description }));
      break;
    }
    case 'Contacts': {
      data = getContactDropdownOptions({
        contactAddressBooks,
        linkedCustomerId,
        linkedVendorId,
        responding,
        linkField,
        globalAddressBooks,
      });
      placeholder = 'Select Contact';
      break;
    }
    case 'Custom': {
      data = customOptions;
      break;
    }
    case 'Forms': {
    // Prevent infinite form workflow loop
      data = formTemplates.filter((ft) => ft.active
      && divisionSet.has(ft?.divisionId)
      && ft.id !== templateId);
      placeholder = 'Select Form Workflow';
      break;
    }
    case 'Labels': {
      const subDataTypeSet = new Set(subDataType);
      data = labels
        .filter((label) => !subDataType?.length || subDataTypeSet.has(label.type))
        .map((label) => ({ id: label.id, name: label.title }));
      placeholder = 'Select Label';
      break;
    }
    case 'CompletedForms': {
      data = formatFormDropdownList({
        formList: forms,
        projectIdMap,
        projectId,
        shouldFilterByProjects: true,
      });
      placeholder = 'Select Form';
      break;
    }
    case 'Cards': {
      data = boardCardsForDropdown?.[subDataType]?.cards?.map((card) => ({
        id: card.id,
        name: card.name,
        subNames: [
          card.statusTitle ?? '',
          card.lastUpdated ? DateTime.fromMillis(card.lastUpdated).toLocaleString(DateTime.DATETIME_MED) : '',
        ],
      })) ?? [];
      placeholder = 'Select Card';
      break;
    }
    case 'Buckets': {
      const buckets = bucketTemplateMap?.[subDataType] ?? [];
      const filterIds = Buckets.getFilterIdsForBucketDropdown({
        buckets,
        customerId,
        projectId,
        selectedBucketTypeToIdMap,
        subDataType,
      });

      data = buckets;
      allData = buckets;

      if (filterIds.length) {
        data = Buckets.getLinkedBuckets(buckets, filterIds, ['customers', 'projects', ...selectedBucketTypes]).allBuckets;
      }
      placeholder = 'Select Bucket';
      break;
    }
    case 'PayableSubContracts': {
      data = formatFormDropdownList({
        formList: subContractForms,
        projectIdMap,
        vendorIdMap,
        projectId,
        shouldFilterByProjects: true,
      });
      placeholder = 'Select Sub-Contract';
      break;
    }
    case 'ScheduleCategories': {
      data = scheduleCategories;
      placeholder = 'Select Schedule Category';
      break;
    }
    case 'Divisions': {
      // divisionMap already contains only divisions the user has access to
      data = Object.values(divisionMap)
        .map((division) => ({
          id: division.id,
          name: division.name,
        }));
      placeholder = 'Select Division';
      break;
    }
    case 'Boards': {
      data = boardsForDropdown
        .filter((board) => !boardCardTypeFilter || board.cardTypeId === boardCardTypeFilter)
        .map((board) => ({ id: board.id, name: board.title }));
      placeholder = 'Select Board';
      break;
    }
    default: {
      data = [];
      break;
    }
  }

  data = data.sort(sortByObjProperty(sortProperty));

  return {
    data,
    placeholder,
    allData,
  };
};

export const updateStackedDates = (oldDate) => {
  const safeDatesArray = typeof oldDate === 'string' ? oldDate.split(', ') : [];
  if (safeDatesArray.length) {
    return oldDate;
  }
  return DateTime.local().toISO();
};

export const calculationTableRender = (col, isDisplay) => (value) => {
  const fallback = (col.isCurrency ? '0.00' : '0');
  let displayValue = value ?? fallback;
  if (col.isCurrency) {
    if (isDisplay) displayValue = displayValue?.toString().replace(/\$\s?/g, '') ?? '';
    displayValue = currencyFormatter(displayValue);
  }
  return displayValue;
};

export const getCalculationTableColumn = (col, { width, isDisplay }) => {
  const res = {
    title: <div>{col.name}</div>,
    dataIndex: col.name,
    render: calculationTableRender(col, isDisplay),
  };
  if (width) res.width = width;
  return res;
};

export const getCustomTableColumn = (col, {
  width,
  isDisplay,
  preventEdits,
  requiredColumns,
  onChange,
  responses,
}) => {
  const isReadOnly = isDisplay || preventEdits;

  const render = (val, record) => {
    if (isCustomDateColumn(col)) {
      return (
        <CustomDateColumn
          col={col}
          value={val}
          record={record}
          onChange={onChange}
          isReadOnly={isReadOnly}
          responses={responses}
        />
      );
    }

    if (isReadOnly) {
      if (col.isCurrency) {
        const numeric = Number.parseFloat(currencyParser(String(val ?? '')));
        return currencyFormatter(Number.isNaN(numeric) ? 0 : numeric);
      }
      return val ?? '';
    }

    if (col.isCurrency) {
      const numeric = Number.parseFloat(currencyParser(String(val ?? '')));
      const currencyValue = Number.isNaN(numeric) ? undefined : numeric;
      return (
        <OnTraccrNumberInput
          key={record.id}
          formatter={currencyFormatter}
          parser={currencyParser}
          step={0.01}
          precision={2}
          onChange={(value) => onChange(record, value)}
          value={currencyValue}
        />
      );
    }

    if (col.isNumeric) {
      return (
        <OnTraccrNumberInput
          key={record.id}
          onChange={(value) => onChange(record, value)}
          value={val}
        />
      );
    }

    return (
      <OnTraccrTextInput
        textarea
        value={val}
        onChange={(e) => onChange(record, e.target.value)}
      />
    );
  };

  const res = {
    title: getColumnTitle({ requiredColumns, title: col.name }),
    dataIndex: col.key,
    render,
  };
  if (width) res.width = width;
  return res;
};

export const LINE_NUMBER_COLUMN = {
  title: '',
  dataIndex: 'lineNumber__',
  fixed: 'left',
  width: 30,
  onHeaderCell: () => ({
    style: {
      borderRight: '1px solid #e8e8e8',
    },
  }),
  onCell: () => ({
    style: {
      textAlign: 'center',
      backgroundColor: '#fafafa',
      borderRight: '1px solid #e8e8e8',
    },
  }),
  render: (_text, _record, index) => index + 1,
};

export const createHandleReloadPreload = ({ responses, id, onReload }) => () => {
  const currentValues = responses?.[id]?.values ?? [];
  const hasUserAddedEntries = currentValues.some((value) => !value.isPreloaded);

  if (hasUserAddedEntries) {
    CustomConfirmModal({
      title: 'Reload Preloaded Data?',
      content: 'This will remove any entries you have manually added to the table. Only preloaded entries will remain. Do you want to continue?',
      okText: 'Reload',
      cancelText: 'Cancel',
      onOk: () => {
        onReload();
      },
    });
  } else {
    onReload();
  }
};

// Clear vendor on custom rows if the vendor is not in the form's division
export const clearVendorOnCustomRows = ({ selected, vendors, divisions }) => {
  if (!divisions?.length) return { changed: false, nextSelected: selected };
  const divisionSet = new Set(divisions);
  let changed = false;
  const nextSelected = selected.map((row) => {
    if (!row.isCustom || !row.vendorId) return row;
    const vendor = vendors[row.vendorId];
    const vendorInDivision = vendor?.divisionIds?.some((divId) => divisionSet.has(divId));
    if (!vendorInDivision) {
      changed = true;
      return { ...row, vendorId: null };
    }
    return row;
  });

  return { changed, nextSelected };
};

export const rematchProjectMaterials = ({
  skipInitial,
  lastRematchedProjectId,
  projectId,
  projectMaterials,
}) => {
  const stillLoadingMaterials = projectId && projectMaterials === undefined;

  if (skipInitial) {
    if (stillLoadingMaterials) {
      return {
        changed: false,
        nextSkipInitial: true,
        nextLastRematchedProjectId: lastRematchedProjectId,
      };
    }

    return {
      changed: false,
      nextSkipInitial: false,
      nextLastRematchedProjectId: projectId,
    };
  }

  if (
    lastRematchedProjectId === projectId
    || stillLoadingMaterials
  ) {
    return {
      changed: false,
      nextSkipInitial: false,
      nextLastRematchedProjectId: lastRematchedProjectId,
    };
  }

  return {
    changed: true,
    nextSkipInitial: false,
    nextLastRematchedProjectId: projectId,
  };
};

// Clear costcodes on table rows when the linked project dropdown selection changes
export const clearCostcodesOnLinkedProjectChange = ({
  selected,
  linkedProjectId,
  prevLinkedProjectId,
  linkProjectCostCodes,
  responding,
  isDisplay,
}) => {
  if (!responding || isDisplay || !linkProjectCostCodes) {
    return {
      changed: false,
      nextSelected: selected,
      nextPrevLinkedProjectId: linkedProjectId,
    };
  }

  if (prevLinkedProjectId === linkedProjectId) {
    return {
      changed: false,
      nextSelected: selected,
      nextPrevLinkedProjectId: prevLinkedProjectId,
    };
  }

  const hasCostcodes = selected.some((row) => row.costcodeId || row.phaseCostcodeKey);
  if (!hasCostcodes) {
    return {
      changed: false,
      nextSelected: selected,
      nextPrevLinkedProjectId: linkedProjectId,
    };
  }

  const clearedFields = getFormTableCostcodeUpdateFields({ selectedVal: '' });
  return {
    changed: true,
    nextSelected: selected.map((row) => ({ ...row, ...clearedFields })),
    nextPrevLinkedProjectId: linkedProjectId,
  };
};
